const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/** Klasse van de outline-laag. */
export const HD_GATE_PIECE_OUTLINE_LAYER_CLASS = 'hd-hover-gate-piece-outlines';
/** De kleurbron van de ring; `flood-color` volgt de hoverkleur via de chart-SCSS. */
export const HD_GATE_PIECE_OUTLINE_FLOOD_CLASS = 'hd-hover-gate-piece-flood';

/** Vlakvormen die mee het silhouet van een kanaalstuk bepalen (geen tekst/lijnen). */
export const HD_GATE_PIECE_SHAPE_SELECTOR = 'path, rect, polygon, polyline, ellipse, circle';

/** Kanaalgroepen van het asset: een naamloze grijze randlaag gevolgd door de vullagen. */
const CHANNEL_FRAME_SELECTOR = 'g[id^="channel"]';

/** Enkel de geometrie van een vorm gaat mee naar de klonen; stijl, ids en klassen niet. */
const GEOMETRY_ATTRIBUTES = new Set(['d', 'points', 'x', 'y', 'width', 'height', 'rx', 'ry', 'cx', 'cy', 'r']);

const DEFAULT_OUTLINE_WIDTH_PX = 2;

/**
 * Straal (SVG-eenheden, ~de grijze randbreedte) van de morfologische closing
 * vóór de ring: vult de subpixel-gaatjes en -splinters op de knooppunten
 * waar de schuin afgewerkte uiteinden van stukken elkaar net niet sluitend
 * overlappen. Zonder closing tekent de ring daar kleine driehoekjes in het stuk.
 */
const CLOSING_RADIUS_UNITS = 4;

/**
 * Extra rand (schermpixels) rond de gom-klonen. Zonder die rand laten twee
 * vormen met exact dezelfde zijkant (bv. het lange 57-stuk onder het korte)
 * een anti-aliasing-rest achter, die de ring als dun lijntje zou omlijnen.
 */
const ERASER_BLEED_PX = 1;

/** Rechthoek in root-coördinaten van de SVG (viewBox-eenheden). */
interface OutlineRegion {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Outline rond de zichtbare kanaalstukken van de gehighlighte poorten
 * (app-setting "poortstukken highlighten"). Zie
 * `docs/human-design/hd-chart-hover-highlight.md` §11.2.
 *
 * BELANGRIJK — waarom zo omslachtig (Stevens check 2026-10-13, poorten 20, 34,
 * 10 en 57): in het integratiegebied liggen lagen van verschillende poorten
 * over elkaar (het lange 57-stuk onder het korte, of onder de kleur van 10 of
 * 20; corner-caps op de knooppunten). Een outline rond "alle eigen lagen"
 * omlijnde daardoor stukken die visueel bij een andere poort horen. Daarom:
 *
 * 1. **Zichtbaar gebied** — een masker tekent de eigen lagen wit en de lagen
 *    van andere poorten zwart, in documentvolgorde. Wat een latere laag van
 *    een andere poort bedekt, valt zo weg; een latere eigen laag komt terug.
 * 2. **Exacte rand** — een stuk telt mee met zijn grijze rand: voor een
 *    kanaalgroep met randlaag is dat de randvorm zelf (exact zoals het asset
 *    hem tekent, ook de schuine knooppunt-uiteinden); een randloze groep (het
 *    korte 57-stuk ligt op de rand van het lange) wordt in zijn
 *    breedterichting verbreed (zie {@link widenedRootMatrix}), zodat de
 *    afsluiting op de grens tussen twee kleuren recht door de rand loopt.
 * 3. **Ring** — een filter sluit eerst subpixel-gaatjes (closing), rekt het
 *    zichtbare gebied dan op met de outline-breedte en houdt enkel de
 *    buitenste strook over, in de hoverkleur.
 * 4. **Uitsparing** — een tweede masker snijdt alle centers en poortcirkels
 *    weg: de stukken lopen onder hun center door en mogen daar geen rand tekenen.
 *
 * Klonen worden per bron-leaf eenmalig gemaakt (de SVG-geometrie is statisch);
 * een render doet enkel zichtbaarheidschecks, een sortering en `replaceChildren`.
 */
export class HdGatePieceOutlineLayer {
  readonly layer: SVGGElement;
  private readonly paint: SVGGElement;
  private readonly eraseContent: SVGGElement;
  private readonly filter: SVGFilterElement;
  private readonly closingDilate: SVGFEMorphologyElement;
  private readonly closingErode: SVGFEMorphologyElement;
  private readonly morphology: SVGFEMorphologyElement;
  private readonly clonesByLeaf = new WeakMap<Element, { readonly paint: SVGElement; readonly mask: SVGElement }>();
  private readonly backgroundByFrame = new WeakMap<Element, Element | null>();
  private defaultWidenRatio: number | null | undefined;
  /** Omhullende per kloon-bron (root-coördinaten), uit de attributen; `null` als die er niet uit te lezen is. */
  private readonly boundsByLeaf = new WeakMap<Element, OutlineRegion | null>();
  /** Schaal en ringstraal, gemeten bij de eerste render tot {@link invalidateMetrics}. */
  private metrics: { readonly pxPerUnit: number; readonly radius: number } | null = null;

  constructor(
    readonly svg: SVGSVGElement,
    idPrefix: string,
    exclusionLeaves: Iterable<Element>,
  ) {
    const region = this.maskRegion();

    // 4. Uitsparing: zichtbaar overal, behalve centers en poortcirkels.
    const exclusionMask = this.createMask(`${idPrefix}-exclusions`, region);
    const visibleArea = document.createElementNS(SVG_NAMESPACE, 'rect');
    this.setRegion(visibleArea, region);
    visibleArea.setAttribute('fill', '#fff');
    const exclusions = document.createElementNS(SVG_NAMESPACE, 'g');
    exclusions.setAttribute('fill', '#000');
    for (const leaf of exclusionLeaves) {
      exclusions.append(this.cloneGeometry(leaf, this.rootMatrix(leaf)));
    }
    exclusionMask.append(visibleArea, exclusions);

    // 1. Zichtbaar gebied: eigen lagen wit, latere lagen van andere poorten zwart.
    const eraseMask = this.createMask(`${idPrefix}-visible`, region);
    this.eraseContent = document.createElementNS(SVG_NAMESPACE, 'g');
    eraseMask.append(this.eraseContent);

    // 3. Ring: (opgerekt − origineel) in de hoverkleur. De eerste stap snijdt
    // halve-dekking-resten van naden weg (beide kanten van een naad dekken
    // elk maar een deel van de randpixel) zodat er binnenin geen lijntjes
    // ontstaan, en ruimt gom-resten op.
    this.filter = document.createElementNS(SVG_NAMESPACE, 'filter');
    this.filter.id = `${idPrefix}-ring`;
    this.filter.setAttribute('filterUnits', 'userSpaceOnUse');
    this.filter.setAttribute('color-interpolation-filters', 'sRGB');
    const solid = document.createElementNS(SVG_NAMESPACE, 'feComponentTransfer');
    solid.setAttribute('in', 'SourceAlpha');
    solid.setAttribute('result', 'solid');
    const alpha = document.createElementNS(SVG_NAMESPACE, 'feFuncA');
    alpha.setAttribute('type', 'linear');
    alpha.setAttribute('slope', '3');
    alpha.setAttribute('intercept', '-1');
    solid.append(alpha);
    this.closingDilate = this.createMorphology('solid', 'dilate', 'bridged');
    this.closingErode = this.createMorphology('bridged', 'erode', 'closed');
    this.closingDilate.setAttribute('radius', `${CLOSING_RADIUS_UNITS}`);
    this.closingErode.setAttribute('radius', `${CLOSING_RADIUS_UNITS}`);
    this.morphology = this.createMorphology('closed', 'dilate', 'grown');
    const ring = document.createElementNS(SVG_NAMESPACE, 'feComposite');
    ring.setAttribute('in', 'grown');
    ring.setAttribute('in2', 'closed');
    ring.setAttribute('operator', 'out');
    ring.setAttribute('result', 'ring');
    const flood = document.createElementNS(SVG_NAMESPACE, 'feFlood');
    flood.setAttribute('class', HD_GATE_PIECE_OUTLINE_FLOOD_CLASS);
    const colored = document.createElementNS(SVG_NAMESPACE, 'feComposite');
    colored.setAttribute('in2', 'ring');
    colored.setAttribute('operator', 'in');
    this.filter.append(solid, this.closingDilate, this.closingErode, this.morphology, ring, flood, colored);

    const defs = document.createElementNS(SVG_NAMESPACE, 'defs');
    defs.append(exclusionMask, eraseMask, this.filter);
    svg.append(defs);

    this.paint = document.createElementNS(SVG_NAMESPACE, 'g');
    this.paint.setAttribute('fill', '#000');
    this.paint.setAttribute('mask', `url(#${eraseMask.id})`);
    const ringGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    ringGroup.setAttribute('filter', `url(#${this.filter.id})`);
    ringGroup.append(this.paint);

    this.layer = document.createElementNS(SVG_NAMESPACE, 'g');
    this.layer.setAttribute('class', HD_GATE_PIECE_OUTLINE_LAYER_CLASS);
    this.layer.setAttribute('mask', `url(#${exclusionMask.id})`);
    this.layer.setAttribute('pointer-events', 'none');
    this.layer.setAttribute('aria-hidden', 'true');
    this.layer.append(ringGroup);
    // Meteen in de SVG: `updateRing` meet de eigen lagen met `getBBox`, wat
    // buiten het document geen layout heeft. De chart herschikt de volgorde.
    svg.append(this.layer);
  }

  /**
   * Tekent de outline rond het zichtbare deel van de `own`-lagen. `foreign`
   * zijn lagen van andere poorten die er (in documentvolgorde later) overheen
   * kunnen liggen; dubbels en verborgen lagen worden genegeerd.
   */
  render(own: Iterable<Element>, foreign: Iterable<Element> = []): void {
    const ownRegions = new Set<Element>();
    for (const leaf of own) {
      if (this.isVisibleFill(leaf)) ownRegions.add(this.regionShape(leaf));
    }
    if (ownRegions.size === 0) {
      this.clear();
      return;
    }
    const foreignRegions = new Set<Element>();
    for (const leaf of foreign) {
      if (!this.isVisibleFill(leaf)) continue;
      const region = this.regionShape(leaf);
      if (!ownRegions.has(region)) foreignRegions.add(region);
    }

    const ordered = [...ownRegions, ...foreignRegions].sort(
      (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1),
    );
    const maskClones = ordered.map(region => {
      const clone = this.clonesFor(region).mask;
      const isOwn = ownRegions.has(region);
      clone.setAttribute('fill', isOwn ? '#fff' : '#000');
      clone.setAttribute('stroke', isOwn ? 'none' : '#000');
      return clone;
    });
    this.eraseContent.replaceChildren(...maskClones);
    this.paint.replaceChildren(...Array.from(ownRegions, region => this.clonesFor(region).paint));
    this.updateRing(ownRegions);
  }

  /** Schaal en ringbreedte opnieuw meten bij de volgende render (na resize/zoom van de SVG). */
  invalidateMetrics(): void {
    this.metrics = null;
  }

  /**
   * De vorm waarmee een zichtbare vullaag in het silhouet meetelt: de grijze
   * randlaag van zijn kanaalgroep (alle vullagen van één groep horen bij
   * dezelfde poort), of de vullaag zelf (verbreed waar nodig, zie
   * {@link clonesFor}).
   */
  private regionShape(leaf: Element): Element {
    const frame = leaf.closest(CHANNEL_FRAME_SELECTOR);
    return (frame != null ? this.frameBackground(frame) : null) ?? leaf;
  }

  clear(): void {
    if (this.paint.childElementCount === 0) {
      return;
    }
    this.paint.replaceChildren();
    this.eraseContent.replaceChildren();
  }

  /**
   * Ringbreedte (schermpixels → user units) en filterregio rond de eigen lagen.
   *
   * BELANGRIJK — geen `getScreenCTM`/`getBBox` per render, niet terugdraaien: de
   * DOM is op dat moment net gewijzigd (dim-klassen, masker), dus elke
   * geometrie-read dwingt een style+layout-herberekening van de hele pagina af
   * (15-20 ms per hover, de duurste post van de hele hover-highlight; gemeten
   * 2026-10-13). Schaal en breedte komen uit {@link resolveMetrics} (gecachet tot
   * een resize), de regio uit de geometrie van de klonen. Zie de spec
   * ("forceert bij een volgende render geen layout").
   */
  private updateRing(ownRegions: Iterable<Element>): void {
    const { pxPerUnit, radius } = this.resolveMetrics();
    this.morphology.setAttribute('radius', `${radius}`);

    const box = this.unionBounds(ownRegions) ?? this.paint.getBBox();
    const margin = 2 * (radius + CLOSING_RADIUS_UNITS) + 2 / pxPerUnit;
    this.setRegion(this.filter, {
      x: box.x - margin,
      y: box.y - margin,
      width: box.width + 2 * margin,
      height: box.height + 2 * margin,
    });
  }

  private resolveMetrics(): { readonly pxPerUnit: number; readonly radius: number } {
    if (this.metrics) {
      return this.metrics;
    }
    const pxPerUnit = Math.abs(this.svg.getScreenCTM()?.a ?? 0) || 1;
    const widthPx = parseFloat(getComputedStyle(this.svg).getPropertyValue('--hd-hover-outline-width'));
    const radius = (Number.isFinite(widthPx) && widthPx > 0 ? widthPx : DEFAULT_OUTLINE_WIDTH_PX) / pxPerUnit;
    this.metrics = { pxPerUnit, radius };
    return this.metrics;
  }

  /**
   * Omhullende van de eigen lagen uit hun geometrie (geen layout). `null` zodra
   * een laag niet uit de attributen te lezen is; dan valt de aanroeper terug op
   * `getBBox` (komt in het asset niet voor: enkel rects en M/L/Z-paden).
   */
  private unionBounds(regions: Iterable<Element>): OutlineRegion | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const region of regions) {
      this.clonesFor(region);
      const bounds = this.boundsByLeaf.get(region);
      if (bounds == null) {
        return null;
      }
      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    }
    if (!Number.isFinite(minX)) {
      return null;
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** Een zichtbare vullaag: geen grijze randlaag (die zit in de verbreding) en niet verborgen. */
  private isVisibleFill(leaf: Element): boolean {
    return leaf.matches(HD_GATE_PIECE_SHAPE_SELECTOR) && !this.isFrameBackground(leaf) && this.isRendered(leaf);
  }

  /**
   * De render-pass verbergt lagen via inline `display: none` (op de laag zelf
   * of een voorouder-groep); enkel wat zichtbaar is hoort bij het silhouet.
   */
  private isRendered(leaf: Element): boolean {
    for (let node: Element | null = leaf; node != null && node !== this.svg; node = node.parentElement) {
      if ((node as SVGElement).style?.display === 'none') {
        return false;
      }
    }
    return true;
  }

  private clonesFor(leaf: Element): { readonly paint: SVGElement; readonly mask: SVGElement } {
    const cached = this.clonesByLeaf.get(leaf);
    if (cached) {
      return cached;
    }
    const matrix = this.isFrameBackground(leaf) ? this.rootMatrix(leaf) : this.widenedRootMatrix(leaf);
    const mask = this.cloneGeometry(leaf, matrix);
    mask.setAttribute('stroke-width', `${ERASER_BLEED_PX * 2}`);
    mask.setAttribute('vector-effect', 'non-scaling-stroke');
    const clones = { paint: this.cloneGeometry(leaf, matrix), mask };
    this.clonesByLeaf.set(leaf, clones);
    this.boundsByLeaf.set(leaf, this.transformedBounds(leaf, matrix));
    return clones;
  }

  /** Omhullende van de hoekpunten (uit de attributen) na de kloon-transform. */
  private transformedBounds(leaf: Element, matrix: DOMMatrix): OutlineRegion | null {
    const points = this.localPoints(leaf);
    if (points == null || points.length === 0) {
      return null;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of points) {
      const point = matrix.transformPoint(new DOMPoint(x, y));
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** Kale geometrie-kloon met de opgegeven transform (root-coördinaten). */
  private cloneGeometry(leaf: Element, matrix: DOMMatrix): SVGElement {
    const clone = document.createElementNS(SVG_NAMESPACE, leaf.localName) as SVGElement;
    for (const attribute of Array.from(leaf.attributes)) {
      if (GEOMETRY_ATTRIBUTES.has(attribute.name)) {
        clone.setAttribute(attribute.name, attribute.value);
      }
    }
    clone.setAttribute('transform', `matrix(${matrix.a} ${matrix.b} ${matrix.c} ${matrix.d} ${matrix.e} ${matrix.f})`);
    return clone;
  }

  /**
   * Root-transform van een vullaag uit een kanaalgroep zonder randlaag (het
   * korte 57-stuk), met de breedte opgerekt alsof hij zijn eigen grijze rand
   * had. Alle kanaalstukken zijn in hun lokale coördinaten rechthoeken met de
   * breedte langs x; de verbreding mapt het x-bereik van de volledige vulling
   * (alle vullagen van de groep samen, zodat ook de rode/zwarte helften enkel
   * naar buiten groeien) op dat x-bereik maal de rand/vulling-verhouding van
   * de andere kanalen. Heeft de groep toch een randlaag, dan mapt hij op die.
   * Caps buiten een kanaalgroep blijven ongewijzigd: ze vullen enkel de hoek
   * tussen twee stukken.
   */
  private widenedRootMatrix(leaf: Element): DOMMatrix {
    const root = this.rootMatrix(leaf);
    const frame = leaf.closest(CHANNEL_FRAME_SELECTOR);
    if (frame == null || !this.svg.contains(frame)) {
      return root;
    }
    const toLeaf = root.inverse();
    const fill = this.frameFillRange(frame, toLeaf);
    if (fill == null) {
      return root;
    }
    const background = this.frameBackground(frame);
    let target = background == null ? null : this.projectedXRange(background, toLeaf);
    if (target == null) {
      const ratio = this.resolveDefaultWidenRatio();
      if (ratio == null) {
        return root;
      }
      const center = (fill[0] + fill[1]) / 2;
      const half = ((fill[1] - fill[0]) * ratio) / 2;
      target = [center - half, center + half];
    }
    const scale = (target[1] - target[0]) / (fill[1] - fill[0]);
    if (!Number.isFinite(scale) || scale <= 0) {
      return root;
    }
    return root.multiply(new DOMMatrix([scale, 0, 0, 1, target[0] - fill[0] * scale, 0]));
  }

  /** x-bereik van alle vullagen van een kanaalgroep, in het lokale stelsel van een leaf. */
  private frameFillRange(frame: Element, toLeaf: DOMMatrix): [number, number] | null {
    const background = this.frameBackground(frame);
    let range: [number, number] | null = null;
    for (const shape of Array.from(frame.querySelectorAll(HD_GATE_PIECE_SHAPE_SELECTOR))) {
      if (shape === background) continue;
      const projected = this.projectedXRange(shape, toLeaf);
      if (projected == null) continue;
      range = range == null
        ? projected
        : [Math.min(range[0], projected[0]), Math.max(range[1], projected[1])];
    }
    return range != null && range[1] > range[0] ? range : null;
  }

  private projectedXRange(shape: Element, toLeaf: DOMMatrix): [number, number] | null {
    const points = this.localPoints(shape);
    if (points == null || points.length === 0) {
      return null;
    }
    const matrix = toLeaf.multiply(this.rootMatrix(shape));
    let min = Infinity;
    let max = -Infinity;
    for (const [x, y] of points) {
      const projected = matrix.transformPoint(new DOMPoint(x, y)).x;
      min = Math.min(min, projected);
      max = Math.max(max, projected);
    }
    return [min, max];
  }

  /**
   * Hoekpunten uit de attributen (geen `getBBox`: verborgen lagen hebben geen
   * layout). Het asset gebruikt voor kanaalstukken enkel rects en paden met
   * absolute M/L/Z; al de rest valt terug op "niet verbreden".
   */
  private localPoints(shape: Element): Array<[number, number]> | null {
    if (shape.localName === 'rect') {
      const x = parseFloat(shape.getAttribute('x') ?? '0') || 0;
      const y = parseFloat(shape.getAttribute('y') ?? '0') || 0;
      const width = parseFloat(shape.getAttribute('width') ?? '0') || 0;
      const height = parseFloat(shape.getAttribute('height') ?? '0') || 0;
      return [[x, y], [x + width, y], [x, y + height], [x + width, y + height]];
    }
    if (shape.localName !== 'path') {
      return null;
    }
    const d = shape.getAttribute('d') ?? '';
    if (/[^MLZ\d\s,.eE+-]/.test(d)) {
      return null;
    }
    const numbers = (d.match(/-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) ?? []).map(Number);
    const points: Array<[number, number]> = [];
    for (let index = 0; index + 1 < numbers.length; index += 2) {
      points.push([numbers[index], numbers[index + 1]]);
    }
    return points;
  }

  /** De grijze randlaag: het naamloze eerste kind van een kanaalgroep. */
  private frameBackground(frame: Element): Element | null {
    if (!this.backgroundByFrame.has(frame)) {
      const first = frame.firstElementChild;
      const background = first?.localName === 'g' && !first.hasAttribute('id')
        ? first.querySelector(HD_GATE_PIECE_SHAPE_SELECTOR)
        : null;
      this.backgroundByFrame.set(frame, background);
    }
    return this.backgroundByFrame.get(frame) ?? null;
  }

  private isFrameBackground(leaf: Element): boolean {
    const frame = leaf.closest(CHANNEL_FRAME_SELECTOR);
    return frame != null && this.frameBackground(frame) === leaf;
  }

  /** Verhouding rand/vulling van het eerste kanaal met randlaag (voor de randloze groepen). */
  private resolveDefaultWidenRatio(): number | null {
    if (this.defaultWidenRatio === undefined) {
      this.defaultWidenRatio = null;
      for (const frame of Array.from(this.svg.querySelectorAll(CHANNEL_FRAME_SELECTOR))) {
        const background = this.frameBackground(frame);
        const reference = Array.from(frame.querySelectorAll(HD_GATE_PIECE_SHAPE_SELECTOR))
          .find(shape => shape !== background);
        if (background == null || reference == null) continue;
        const toLocal = this.rootMatrix(reference).inverse();
        const fill = this.frameFillRange(frame, toLocal);
        const border = this.projectedXRange(background, toLocal);
        if (fill == null || border == null) continue;
        this.defaultWidenRatio = (border[1] - border[0]) / (fill[1] - fill[0]);
        break;
      }
    }
    return this.defaultWidenRatio;
  }

  /**
   * Cumulatieve transform van de leaf tot de SVG-root, uit de transform-
   * attributen (geen `getCTM`: die vereist layout en faalt voor verborgen
   * lagen). Bewust niet via `consolidate()`, dat het attribuut herschrijft.
   */
  private rootMatrix(leaf: Element): DOMMatrix {
    let matrix = new DOMMatrix();
    for (let node: Element | null = leaf; node != null && node !== this.svg; node = node.parentElement) {
      if (!(node instanceof SVGGraphicsElement)) {
        continue;
      }
      const transforms = node.transform.baseVal;
      let local = new DOMMatrix();
      for (let index = 0; index < transforms.numberOfItems; index++) {
        local = local.multiply(DOMMatrix.fromMatrix(transforms.getItem(index).matrix));
      }
      matrix = local.multiply(matrix);
    }
    return matrix;
  }

  private createMorphology(input: string, operator: 'dilate' | 'erode', result: string): SVGFEMorphologyElement {
    const morphology = document.createElementNS(SVG_NAMESPACE, 'feMorphology');
    morphology.setAttribute('in', input);
    morphology.setAttribute('operator', operator);
    morphology.setAttribute('result', result);
    return morphology;
  }

  private createMask(id: string, region: { x: number; y: number; width: number; height: number }): SVGMaskElement {
    const mask = document.createElementNS(SVG_NAMESPACE, 'mask');
    mask.id = id;
    mask.setAttribute('maskUnits', 'userSpaceOnUse');
    mask.setAttribute('maskContentUnits', 'userSpaceOnUse');
    this.setRegion(mask, region);
    return mask;
  }

  private setRegion(element: Element, region: { x: number; y: number; width: number; height: number }): void {
    element.setAttribute('x', `${region.x}`);
    element.setAttribute('y', `${region.y}`);
    element.setAttribute('width', `${region.width}`);
    element.setAttribute('height', `${region.height}`);
  }

  /** Ruim rond de viewBox, zodat een masker nooit een rand afknipt. */
  private maskRegion(): { x: number; y: number; width: number; height: number } {
    const viewBox = this.svg.viewBox.baseVal;
    if (viewBox == null || viewBox.width === 0 || viewBox.height === 0) {
      return { x: -100000, y: -100000, width: 200000, height: 200000 };
    }
    const margin = Math.max(viewBox.width, viewBox.height);
    return {
      x: viewBox.x - margin,
      y: viewBox.y - margin,
      width: viewBox.width + 2 * margin,
      height: viewBox.height + 2 * margin,
    };
  }
}
